<template>
  <section class="company-legal-panel">
    <div class="company-legal-panel__grid">
      <article class="company-legal-panel__card company-legal-panel__card--wide">
        <div class="company-legal-panel__card-head">
          <div>
            <p class="company-legal-panel__eyebrow">Legal y Fiscal</p>
            <h2 class="company-legal-panel__title">{{ legalFiscalTitle }}</h2>
          </div>

          <button
            v-if="permissions.canEdit"
            class="company-legal-panel__primary-action"
            type="button"
            @click="$emit('edit-record')"
          >
            Editar ficha
          </button>
        </div>

        <p v-if="showLegalFiscalNotApplicableNotice" class="company-legal-panel__reference-kicker">
          Solo referencia
        </p>

        <div class="company-legal-panel__facts" :class="{ 'company-legal-panel__facts--reference': showLegalFiscalNotApplicableNotice }">
          <div class="company-legal-panel__fact">
            <span class="company-legal-panel__fact-label">Inicio de actividades</span>
            <strong class="company-legal-panel__fact-value">{{ formattedStartActivities }}</strong>
          </div>
          <div class="company-legal-panel__fact">
            <span class="company-legal-panel__fact-label">Régimen tributario</span>
            <strong class="company-legal-panel__fact-value">{{ record.tax_regime_name || 'Sin información' }}</strong>
          </div>
          <div class="company-legal-panel__fact">
            <span class="company-legal-panel__fact-label">Tipo facturador</span>
            <strong class="company-legal-panel__fact-value">{{ record.biller_type_name || 'Sin información' }}</strong>
          </div>
          <div class="company-legal-panel__fact">
            <span class="company-legal-panel__fact-label">Tipo organizacional</span>
            <strong class="company-legal-panel__fact-value">{{ record.organizational_type || 'Sin información' }}</strong>
          </div>
        </div>

        <p v-if="showLegalFiscalNotApplicableNotice" class="company-legal-panel__notice">
          Este cliente está configurado para no considerar atributos legales y fiscales en el cálculo de ficha completa. La información visible se conserva solo como referencia.
        </p>
      </article>

      <article class="company-legal-panel__card">
        <div class="company-legal-panel__card-head">
          <div>
            <p class="company-legal-panel__eyebrow">Estado</p>
            <h3 class="company-legal-panel__section-title">Condicion del cliente</h3>
          </div>
        </div>

        <div class="company-legal-panel__status" :class="statusClass">
          {{ company.status || 'Activo' }}
        </div>

        <p class="company-legal-panel__copy">
          El estado del cliente condiciona la edicion operativa y el seguimiento de la ficha.
        </p>
      </article>

      <article class="company-legal-panel__card">
        <div class="company-legal-panel__card-head">
          <div>
            <p class="company-legal-panel__eyebrow">Cobertura</p>
            <h3 class="company-legal-panel__section-title">Pendientes legales</h3>
          </div>
        </div>

        <ul v-if="pendingItems.length" class="company-legal-panel__pending-list">
          <li v-for="item in pendingItems" :key="item">{{ item }}</li>
        </ul>

        <p v-else class="company-legal-panel__copy">
          No hay pendientes legales o fiscales detectados en la ficha principal.
        </p>
      </article>
    </div>
  </section>
</template>

<script>
import { formatDateOnly } from '@/utils/date'

export default {
  name: 'CompanyLegalPanel',
  props: {
    company: {
      type: Object,
      required: true
    },
    record: {
      type: Object,
      required: true
    },
    pendingItems: {
      type: Array,
      default: () => []
    },
    permissions: {
      type: Object,
      required: true
    }
  },
  computed: {
    formattedStartActivities () {
      return formatDateOnly(this.record.start_activities)
    },
    showLegalFiscalNotApplicableNotice () {
      return this.company && this.company.client_type === 'persona_natural' && this.record && this.record.requires_legal_fiscal_info === false
    },
    legalFiscalTitle () {
      return this.showLegalFiscalNotApplicableNotice ? 'Información tributaria y societaria referencial' : 'Resumen tributario y societario'
    },
    statusClass () {
      const status = (this.company.status || 'Activo').toLowerCase()
      if (status === 'inactivo') return 'company-legal-panel__status--inactive'
      if (status.includes('ttg')) return 'company-legal-panel__status--warning'
      if (status === 'esporádico') return 'company-legal-panel__status--muted'
      return 'company-legal-panel__status--active'
    }
  }
}
</script>

<style scoped>
.company-legal-panel__grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 1rem;
}

.company-legal-panel__card {
  grid-column: span 4;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.58);
  border-radius: 26px;
  background: linear-gradient(180deg, rgba(255, 252, 247, 0.98), rgba(246, 239, 231, 0.94));
  box-shadow: 0 20px 50px rgba(43, 34, 24, 0.06);
}

.company-legal-panel__card--wide {
  grid-column: span 12;
}

.company-legal-panel__card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.9rem;
}

.company-legal-panel__eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.72rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(58, 46, 33, 0.48);
}

.company-legal-panel__title,
.company-legal-panel__section-title {
  margin: 0;
  color: #211913;
}

.company-legal-panel__title {
  font-size: 1.2rem;
}

.company-legal-panel__section-title {
  font-size: 1rem;
}

.company-legal-panel__primary-action {
  min-height: 2.5rem;
  padding: 0 0.9rem;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #251d16 0%, #4c3c2d 100%);
  color: #f7f0e5;
}

.company-legal-panel__facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.8rem;
}

.company-legal-panel__reference-kicker {
  display: inline-flex;
  align-items: center;
  margin: 0 0 0.75rem;
  padding: 0.34rem 0.68rem;
  border-radius: 999px;
  background: rgba(184, 139, 59, 0.14);
  color: #7a5925;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.company-legal-panel__facts--reference .company-legal-panel__fact {
  background: rgba(255, 255, 255, 0.28);
  border: 1px dashed rgba(161, 125, 72, 0.28);
}

.company-legal-panel__facts--reference .company-legal-panel__fact-value {
  color: rgba(36, 28, 22, 0.78);
}

.company-legal-panel__notice {
  margin: 0.9rem 0 0;
  padding: 0.8rem 0.95rem;
  border-radius: 18px;
  background: rgba(184, 139, 59, 0.12);
  color: #775227;
  line-height: 1.55;
}

.company-legal-panel__fact {
  padding: 0.85rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.62);
}

.company-legal-panel__fact-label {
  display: block;
  margin-bottom: 0.25rem;
  color: rgba(58, 46, 33, 0.52);
  font-size: 0.74rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.company-legal-panel__fact-value {
  color: #241c16;
  font-size: 1.02rem;
}

.company-legal-panel__status {
  display: inline-flex;
  align-items: center;
  min-height: 2.2rem;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  font-size: 0.84rem;
  font-weight: 700;
}

.company-legal-panel__status--active {
  background: rgba(77, 140, 97, 0.14);
  color: #2f6a43;
}

.company-legal-panel__status--inactive {
  background: rgba(168, 78, 78, 0.12);
  color: #8a3f3f;
}

.company-legal-panel__status--warning {
  background: rgba(184, 139, 59, 0.14);
  color: #8a6520;
}

.company-legal-panel__status--muted {
  background: rgba(95, 107, 122, 0.14);
  color: #5a6574;
}

.company-legal-panel__copy {
  margin: 0.75rem 0 0;
  color: rgba(45, 35, 26, 0.62);
  line-height: 1.55;
}

.company-legal-panel__pending-list {
  margin: 0;
  padding-left: 1rem;
  color: #5a4a3b;
}

@media (max-width: 980px) {
  .company-legal-panel__card,
  .company-legal-panel__card--wide {
    grid-column: span 12;
  }
}

@media (max-width: 640px) {
  .company-legal-panel__facts {
    grid-template-columns: 1fr;
  }
}
</style>

